// ============================================================
// BATTLEFIELD BOUNDARY - the shape of the arena.
//
// A square yard, a round pit, a long corridor, an L-shaped hab block:
// all one ring of ground-plane vertices, generated the same way solids
// are, with independent x/z scaling to reach rectangles and ellipses. A
// hand-authored `vertices` ring overrides the generator entirely, which
// is what makes non-convex arenas possible.
//
// Pure. Zero React dependency.
// ============================================================
import type { Vec3 } from './types';

export interface Vec2 {
  x: number;
  z: number;
}

export interface Boundary {
  /** Vertices in the generated ring. 4 is a square, 24 reads as round. */
  sides: number;
  /** Apothem in metres, before scaling. */
  radius: number;
  /** Independent stretch: 4 sides at scaleX 2 is a rectangle. */
  scaleX: number;
  scaleZ: number;
  /** Degrees about the vertical axis. */
  rotation: number;
  centre: Vec2;
  /** Metres per painted tile. */
  tileSize: number;
  /** An explicit ring. When present, everything above is ignored. */
  vertices?: Vec2[];
  /**
   * Which catalogue entry this arena was made from - see arenaShapes.ts.
   * Absent on legacy maps, which infer from sides+scaleX as they always
   * did. Persistence passes it through untouched.
   */
  shape?: string;
}

const DEG = Math.PI / 180;

/** A 20m square on one-metre tiles - a skirmish, not a battle. */
export function makeBoundary(overrides: Partial<Boundary> = {}): Boundary {
  return {
    sides: 4,
    radius: 10,
    scaleX: 1,
    scaleZ: 1,
    rotation: 0,
    centre: { x: 0, z: 0 },
    tileSize: 1,
    ...overrides,
    ...(overrides.centre ? { centre: { ...overrides.centre } } : {}),
    ...(overrides.vertices ? { vertices: overrides.vertices.map((v) => ({ ...v })) } : {}),
  };
}

/**
 * The arena outline on the ground plane, in order.
 *
 * Radius is the APOTHEM, matching `Solid` - a 4-sided boundary of radius
 * 10 is a 20×20 m yard, not a 28 m diamond.
 */
export function boundaryOutline(boundary: Boundary): Vec3[] {
  if (boundary.vertices?.length) {
    return boundary.vertices.map((v) => ({ x: v.x, y: 0, z: v.z }));
  }
  const n = Math.max(3, Math.floor(boundary.sides));
  const step = (2 * Math.PI) / n;
  const r = boundary.radius / Math.cos(step / 2);
  const out: Vec3[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i + 0.5) * step + boundary.rotation * DEG;
    out.push({
      x: boundary.centre.x + r * Math.cos(a) * boundary.scaleX,
      y: 0,
      z: boundary.centre.z + r * Math.sin(a) * boundary.scaleZ,
    });
  }
  return out;
}

export interface Extent2 {
  min: Vec2;
  max: Vec2;
}

/** Axis-aligned extent of the arena - what the camera frames on reset. */
export function boundaryExtent(boundary: Boundary): Extent2 {
  const ring = boundaryOutline(boundary);
  const min: Vec2 = { x: Infinity, z: Infinity };
  const max: Vec2 = { x: -Infinity, z: -Infinity };
  for (const p of ring) {
    min.x = Math.min(min.x, p.x);
    min.z = Math.min(min.z, p.z);
    max.x = Math.max(max.x, p.x);
    max.z = Math.max(max.z, p.z);
  }
  return { min, max };
}

/**
 * The middle of the extent, not the vertex mean.
 *
 * The camera should frame what you can SEE, and for an L-shaped arena the
 * vertex mean drifts towards whichever leg has more corners.
 */
export function boundaryCentre(boundary: Boundary): Vec2 {
  const e = boundaryExtent(boundary);
  return { x: (e.min.x + e.max.x) / 2, z: (e.min.z + e.max.z) / 2 };
}

/**
 * The outline, worked out once per boundary: the reach flood asks `boundaryContains` for every tile, and rebuilt
 * each time the outline was a large share of a drag's stall (the perf overhaul). Boundaries are never mutated - an
 * edit makes a new one - so the boundary object is the key.
 */
const outlines = new WeakMap<Boundary, Vec3[]>();
function outlineOf(boundary: Boundary): Vec3[] {
  let ring = outlines.get(boundary);
  if (!ring) outlines.set(boundary, (ring = boundaryOutline(boundary)));
  return ring;
}

/**
 * Point-in-polygon by ray casting - works for concave rings, which the
 * even-odd rule handles and a convex half-plane test would not.
 *
 * Points exactly ON an edge are undefined by nature; `clampToBoundary`
 * therefore nudges inward rather than landing on the line.
 */
export function boundaryContains(boundary: Boundary, x: number, z: number): boolean {
  const ring = outlineOf(boundary);
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    const straddles = a.z > z !== b.z > z;
    if (straddles && x < ((b.x - a.x) * (z - a.z)) / (b.z - a.z) + a.x) inside = !inside;
  }
  return inside;
}

/** Closest point on segment a→b to p, as a Vec2. */
function closestOnSegment(p: Vec2, a: Vec3, b: Vec3): Vec2 {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const lenSq = dx * dx + dz * dz;
  if (lenSq < 1e-12) return { x: a.x, z: a.z };
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / lenSq));
  return { x: a.x + t * dx, z: a.z + t * dz };
}

/** How far inside the edge a clamped point lands - a micron, but not zero. */
const INSET = 1e-6;

/**
 * Bring a point inside the arena, on the nearest edge.
 *
 * The result is inset very slightly so that `boundaryContains` agrees
 * with it: a point exactly on the line is a coin toss for ray casting,
 * and a token that "left the map" while sitting on its own edge would be
 * a genuinely baffling bug.
 *
 * Which way is inward is decided by ASKING rather than by winding order.
 * A hand-authored ring can be wound either way, and a wrong guess pushes
 * the token further out - the failure this exists to prevent.
 */
export function clampToBoundary(boundary: Boundary, x: number, z: number): Vec2 {
  if (boundaryContains(boundary, x, z)) return { x, z };
  const { best, perp } = nearestEdgePoint(boundaryOutline(boundary), x, z);

  for (const step of [
    { x: perp.x * INSET, z: perp.z * INSET },
    { x: -perp.x * INSET, z: -perp.z * INSET },
  ]) {
    const p = { x: best.x + step.x, z: best.z + step.z };
    if (boundaryContains(boundary, p.x, p.z)) return p;
  }

  // The closest point was a CORNER, where neither perpendicular leads
  // inside. Creep towards the middle instead.
  const c = boundaryCentre(boundary);
  const len = Math.hypot(c.x - best.x, c.z - best.z) || 1;
  return { x: best.x + ((c.x - best.x) / len) * INSET, z: best.z + ((c.z - best.z) / len) * INSET };
}

/** The closest point on the outline to (x, z), and the unit normal of the edge it lies on. */
function nearestEdgePoint(ring: Vec3[], x: number, z: number): { best: Vec2; perp: Vec2 } {
  let best: Vec2 = { x, z };
  let bestDist = Infinity;
  let perp: Vec2 = { x: 0, z: 0 };
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[j];
    const b = ring[i];
    const hit = closestOnSegment({ x, z }, a, b);
    const d = Math.hypot(hit.x - x, hit.z - z);
    if (d < bestDist) {
      bestDist = d;
      best = hit;
      const len = Math.hypot(b.x - a.x, b.z - a.z) || 1;
      perp = { x: (b.z - a.z) / len, z: -(b.x - a.x) / len };
    }
  }
  return { best, perp };
}

/** Metres per tile, never zero. */
export const tileOf = (boundary: Pick<Boundary, 'tileSize'>): number => (boundary.tileSize > 0 ? boundary.tileSize : 1);
